import {
  SlComponent,
  SlListChild,
  SlPaginatedPage,
} from '@varo/graphql-schema-types';
import { useCallback, useEffect, useState } from 'react';
import smartViewTransactionsQuery from 'services/graphql/queries/smartViewTransactions';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { useQuery } from 'urql';

// NOTE:
// `SlMutableComponent` and the implementation of `getMergedSmartLedgerComponents`
// in this file should be factored out of the code base and removed.
//
// while fixing a bug in `useGetRecentTransactions` this code was copied here to
// preserve the current implementation while addressing bugs related to these patterns.
// see https://varomoney.atlassian.net/browse/JT-129167
export type SlMutableComponent = SlComponent & {
  transactions?: Array<SlListChild>;
};

/*
 * merges SL components (months) based on 'id'
 *   - if the same 'id' is found txs will be concatenated
 *   - if there is no 'id' match new month will be appended
 */
export const getMergedSmartLedgerComponents = (
  smartLedgerComponents: Array<SlMutableComponent>,
  newChildren?: Array<SlMutableComponent>,
) => {
  const mergedComponents = smartLedgerComponents && [...smartLedgerComponents];

  newChildren?.forEach((slComp) => {
    const slComponent = Object.assign({}, slComp);
    slComponent.transactions = [...slComponent.children];

    const currentMatch = mergedComponents?.find(
      (cur) => cur.id === slComponent.id,
    );

    if (currentMatch) {
      currentMatch.transactions = currentMatch.transactions
        ? [...currentMatch.transactions, ...slComponent.transactions]
        : slComponent.transactions;
    } else {
      mergedComponents?.push(slComponent);
    }
  });

  return mergedComponents;
};

const useGetSmartViewTransactions = (
  accountId: string,
  categoryId: string | null | undefined,
) => {
  const [pagingToken, setPagingToken] = useState('');
  const [fetching, setFetching] = useState(false);
  const [isNewCategoryDataLoading, setIsNewCategoryDataLoading] =
    useState(false);
  const [smartViewTransactions, setSmartLedgerComponents] =
    useState<Array<SlMutableComponent>>();

  const [lastFetchedCategoryId, setLastFetchedCategoryId] =
    useState(categoryId);

  const [{ data, fetching: loading, error }, refetchSmartView] = useQuery<{
    smartViewTransactions: SlPaginatedPage;
  }>({
    query: smartViewTransactionsQuery,
    variables: {
      input: {
        accountId: accountId,
        categoryId: categoryId,
        pagingToken: pagingToken,
      },
    },
    requestPolicy: 'network-only',
  });

  const refresh = useCallback(
    (cat: string) => {
      setSmartLedgerComponents([]);
      if (!fetching && !loading && !error) {
        if (lastFetchedCategoryId !== cat) {
          setIsNewCategoryDataLoading(true);
        }
        setFetching(true);
        setLastFetchedCategoryId(cat);
        refetchSmartView({
          variables: {
            accountId: accountId,
            categoryId: cat || lastFetchedCategoryId || categoryId,
            pagingToken: '',
          },
        });
      }
    },
    [
      accountId,
      categoryId,
      error,
      fetching,
      lastFetchedCategoryId,
      loading,
      refetchSmartView,
    ],
  );

  useEffect(() => {
    const newChildren = data?.smartViewTransactions?.children;

    if (fetching && newChildren && smartViewTransactions) {
      setSmartLedgerComponents(
        getMergedSmartLedgerComponents(smartViewTransactions, newChildren),
      );
      if (data?.smartViewTransactions?.pagingToken) {
        setPagingToken(data.smartViewTransactions.pagingToken);
      }
      setFetching(false);
    } else {
      setPagingToken('');
      const newChild = data?.smartViewTransactions?.children;

      if (smartViewTransactions?.length && isNewCategoryDataLoading) {
        setIsNewCategoryDataLoading(false);
      }

      setSmartLedgerComponents(newChild);
    }
  }, [data, fetching, isNewCategoryDataLoading, smartViewTransactions]);

  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const errorModel = error?.message
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: defaultTitle,
        messageBody: error.message ?? defaultMessage,
      }
    : null;

  return {
    transactions: smartViewTransactions,
    transactionError: errorModel,
    loadingTransactions: smartViewTransactions ? false : loading,
    loadingMoreData: smartViewTransactions ? fetching : false,
    SmartViewTransactions: refresh,
    isNewCategoryDataLoading: loading,
    pagingToken: pagingToken,
  };
};

export default useGetSmartViewTransactions;
